import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import StorageIcon from '@material-ui/icons/Storage';
import ListAltIcon from '@material-ui/icons/ListAlt';
import AirflowIcon from '@material-ui/icons/Toys';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import { isApprover } from '../portal-access/rules';
import { usePortalAccess } from '../portal-access/usePortalAccess';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { me, has } = usePortalAccess();
  const showCatalog = has('menu.catalog');
  const configApi = useApi(configApiRef);
  const airflowDevUrl = configApi.getOptionalString('app.airflowDevUrl');
  const airflowProdUrl = configApi.getOptionalString('app.airflowProdUrl');

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        {showCatalog && (
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
        )}
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {showCatalog && (
            <>
              <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
              <MyGroupsSidebarItem
                singularTitle="My Group"
                pluralTitle="My Groups"
                icon={GroupIcon}
              />
              <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
              <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
              <SidebarItem
                icon={CreateComponentIcon}
                to="create"
                text="Create..."
              />
              <SidebarDivider />
            </>
          )}
          <SidebarItem icon={HomeIcon} to="home" text="Home" />
          {has('menu.data-hub') && <SidebarItem icon={StorageIcon} to="data-hub" text="Data Hub" />}
          {has('menu.registry') && <SidebarItem icon={ListAltIcon} to="registry" text="Registry" />}
          {has('menu.airflow-link') && (airflowDevUrl || airflowProdUrl) && (
            <SidebarItem icon={AirflowIcon} text="Airflow">
              <SidebarSubmenu title="Airflow">
                {airflowDevUrl && (
                  <SidebarSubmenuItem title="DEV" to={airflowDevUrl} />
                )}
                {airflowProdUrl && (
                  <SidebarSubmenuItem title="PROD" to={airflowProdUrl} />
                )}
              </SidebarSubmenu>
            </SidebarItem>
          )}
          <SidebarItem icon={VpnKeyIcon} to="access-request" text="권한 신청" />
          {isApprover(me) && <SidebarItem icon={HowToRegIcon} to="access-approval" text="권한 승인" />}
          <SidebarDivider />
          <SidebarScrollWrapper />
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        {showCatalog && <NotificationsSidebarItem />}
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
